/**
 * i18n.js — i18next configuration using DB-backed HTTP backend.
 *
 * Translation loading chain:
 *   1. Requested language   (e.g. 'fr')
 *   2. English fallback     (key has FR value missing → show EN)
 *   3. Key itself           (no EN value either → show the raw key)
 *
 * Languages are stored in PostgreSQL and served via
 * GET /api/translations/{lang}/{namespace}  (public endpoint).
 *
 * Adding a new language requires zero frontend code:
 *   Admin adds language in /admin/translations, fills keys → done.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import BACKEND_URL from './utils/backendUrl';

// Keep <html lang> in sync so the browser hyphenates and reads text in the right language
i18n.on('languageChanged', lng => { if (typeof document !== 'undefined') document.documentElement.lang = (lng || 'en').split('-')[0]; });



i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Supported + fallback
    fallbackLng: 'en',
    supportedLngs: false,   // false = allow any language code (DB-driven)
    load: 'languageOnly',   // 'en-GB' → 'en'

    // Namespaces
    ns: ['common', 'nav', 'auth', 'vehicle', 'order', 'errors', 'alerts', 'admin', 'dealer', 'lco', 'customer'],
    defaultNS: 'common',

    // HTTP backend: fetch from our FastAPI API
    backend: {
      loadPath: `${BACKEND_URL}/api/translations/{{lng}}/{{ns}}`,
      addPath: false,           // don't POST missing keys
      crossDomain: true,
      requestOptions: {
        mode: 'cors',
        credentials: 'omit',
      },
    },

    // Detection: localStorage key 'fv-lang'
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'fv-lang',
      // 'en-IN' → 'en': the language is also the number / date format locale, and a browser region (en-IN) would
      // format euros in lakh (€1,10,243) — QA FVD-003. Also cleans a region code already stored in fv-lang.
      convertDetectedLanguage: lng => (lng || 'en').split('-')[0],
    },

    // Keys are flat in the DB ("dealers.page_title" is one key, not a nested path)
    keySeparator: false,

    interpolation: {
      escapeValue: false,   // React escapes by default
    },

    // Key fallback: if value is missing, return the key itself
    saveMissing: false,
    missingKeyHandler: false,

    react: {
      useSuspense: false,   // avoid Suspense requirement
    },
  });

// Development only: lets tooling list the keys a page renders without a translation
if (process.env.NODE_ENV === 'development') window.__fvI18n = i18n;

export default i18n;
